import React, { useMemo, useRef, useState } from 'react'
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline'

export const POLICY_NUMBER_OPTIONS = [
  '93748006',
  '93767995',
  '93767999',
  '93768000',
  '93768002',
  '93768004',
  '93768007',
] as const

function sortPolicyNumbersAsc(options: string[]): string[] {
  return [...options].sort((a, b) => {
    const na = Number(a)
    const nb = Number(b)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb
    return a.localeCompare(b, 'pt-BR')
  })
}

interface PolicyNumberComboboxProps {
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export const PolicyNumberCombobox: React.FC<PolicyNumberComboboxProps> = ({
  value,
  onChange,
  placeholder = 'Pesquisar apólice...',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  const allOptions = useMemo(() => {
    const options =
      value && !POLICY_NUMBER_OPTIONS.includes(value as (typeof POLICY_NUMBER_OPTIONS)[number])
        ? [value, ...POLICY_NUMBER_OPTIONS]
        : [...POLICY_NUMBER_OPTIONS]
    return sortPolicyNumbersAsc(options)
  }, [value])

  const filtered = useMemo(() => {
    const term = query.trim()
    if (!term) return allOptions
    return allOptions.filter(opt => opt.includes(term))
  }, [allOptions, query])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setOpen(true)
  }

  const handleSelect = (option: string | null) => {
    onChange(option)
    setQuery('')
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setQuery('')
    setOpen(false)
  }

  const handleFocus = () => {
    setQuery('')
    setOpen(true)
  }

  const handleBlur = () => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setOpen(false)
        setQuery('')
      }
    }, 150)
  }

  const inputDisplayValue = open ? query : (value ?? '')
  const showDropdown = open

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={inputDisplayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={value ? undefined : placeholder}
          autoComplete="off"
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 pr-16 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-1 gap-0.5">
          {value && !disabled && (
            <button
              type="button"
              onMouseDown={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Remover apólice"
              tabIndex={-1}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onMouseDown={e => {
              e.preventDefault()
              if (open) setOpen(false)
              else {
                (e.currentTarget.previousElementSibling as HTMLElement | null)?.focus?.()
                setOpen(true)
              }
            }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={disabled}
            tabIndex={-1}
          >
            <ChevronUpDownIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showDropdown && (
        <ul className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg text-sm focus:outline-none">
          <li
            onMouseDown={e => { e.preventDefault(); handleSelect(null) }}
            className="cursor-pointer select-none px-3 py-2 text-gray-500 dark:text-gray-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 border-b border-gray-100 dark:border-gray-700"
          >
            — Nenhuma —
          </li>

          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-gray-400 dark:text-gray-500 select-none">
              Nenhuma apólice encontrada.
            </li>
          ) : (
            filtered.map(option => (
              <li
                key={option}
                onMouseDown={e => { e.preventDefault(); handleSelect(option) }}
                className={`cursor-pointer select-none px-3 py-2 hover:bg-primary-50 dark:hover:bg-primary-900/30 ${
                  option === value
                    ? 'font-medium text-primary-700 dark:text-primary-300 bg-primary-50/60 dark:bg-primary-900/20'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {option}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
